import React, { useMemo, useState } from 'react';
import type { SuperadminOverview, UsageBucket } from '../hooks/useAdminApi';

interface Props {
  overview: SuperadminOverview;
  isDarkMode: boolean;
}

const money = (value: number): string => value.toLocaleString('de-DE', {
  style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2,
});

const number = (value: number): string => value.toLocaleString('de-DE', { maximumFractionDigits: 1 });
const clampPercent = (value: number, total: number): number => total > 0 ? Math.min(100, Math.max(0, (value / total) * 100)) : 0;

const weekTime = (bucket: UsageBucket): number => {
  const parsed = Date.parse(bucket.week_start || '');
  return Number.isNaN(parsed) ? 0 : parsed;
};

export default function SuperadminUsageCharts({ overview, isDarkMode: dark }: Props) {
  const [weekCount, setWeekCount] = useState(8);
  const weeks = useMemo(() => {
    const chronological = [...(overview.weekly_usage || [])].sort((a, b) => weekTime(a) - weekTime(b));
    return chronological.slice(-weekCount);
  }, [overview.weekly_usage, weekCount]);

  const maxCredits = Math.max(1, ...weeks.map(bucket => Number(bucket.liveavatar_credits || 0)));
  const maxOpenAi = Math.max(0.01, ...weeks.map(bucket => Number(bucket.estimated_openai_cost_eur || 0)));
  const billing = overview.billing_summary;
  const included = Number(billing?.included_credits ?? overview.pricing?.liveavatar?.included_credits ?? 1000);
  const consumed = Number(billing?.consumed_credits ?? billing?.total_internal_credits ?? overview.totals.liveavatar_credits ?? 0);
  const utilization = Number(billing?.utilization_percent ?? billing?.utilization_pct ?? clampPercent(consumed, included));
  const text = dark ? 'text-gray-100' : 'text-slate-950';
  const muted = dark ? 'text-gray-400' : 'text-slate-500';
  const panel = dark ? 'border-gray-800 bg-gray-950/45' : 'border-slate-200 bg-white';

  const chartSummary = weeks.length
    ? `${weeks.length} neueste Wochen. LiveAvatar insgesamt ${number(weeks.reduce((sum, item) => sum + Number(item.liveavatar_credits || 0), 0))} Credits; OpenAI geschätzt ${money(weeks.reduce((sum, item) => sum + Number(item.estimated_openai_cost_eur || 0), 0))}.`
    : 'Noch keine Wochendaten vorhanden.';

  return (
    <section aria-labelledby="usage-trends-title" className="grid grid-cols-1 gap-4 xl:grid-cols-12">
      <div className={`rounded-xl border p-5 shadow-sm xl:col-span-8 ${panel}`}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-orange-500">Verbrauchstrend</p>
            <h3 id="usage-trends-title" className={`mt-1 text-base font-semibold ${text}`}>LiveAvatar Credits & OpenAI Kosten</h3>
            <p className={`mt-1 text-xs ${muted}`}>Neueste Wochen, chronologisch. Die Achsen bleiben bewusst getrennt.</p>
          </div>
          <div className={`inline-flex w-fit rounded-lg border p-1 ${dark ? 'border-gray-700 bg-gray-900' : 'border-slate-200 bg-slate-50'}`} aria-label="Zeitraum auswählen">
            {[4, 8, 12].map(count => (
              <button
                key={count}
                type="button"
                aria-pressed={weekCount === count}
                onClick={() => setWeekCount(count)}
                className={`min-h-[44px] rounded-md px-3 text-xs font-semibold transition focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                  weekCount === count
                    ? 'bg-orange-500 text-white shadow-sm'
                    : dark ? 'text-gray-400 hover:text-white' : 'text-slate-600 hover:text-slate-950'
                }`}
              >{count}W</button>
            ))}
          </div>
        </div>

        <p className="sr-only">{chartSummary}</p>
        {weeks.length ? (
          <div className="mt-6 grid min-h-[220px] grid-cols-[repeat(auto-fit,minmax(42px,1fr))] items-end gap-2" role="img" aria-label={chartSummary}>
            {weeks.map((bucket, index) => {
              const credits = Number(bucket.liveavatar_credits || 0);
              const openAi = Number(bucket.estimated_openai_cost_eur || 0);
              const creditHeight = Math.max(2, clampPercent(credits, maxCredits));
              const costHeight = Math.max(2, clampPercent(openAi, maxOpenAi));
              return (
                <div key={`${bucket.week_start || index}`} className="group flex min-w-0 flex-col items-center gap-2">
                  <div className={`relative flex h-40 w-full max-w-[54px] items-end justify-center gap-1 rounded-md border px-1.5 pt-6 ${dark ? 'border-gray-800 bg-gray-900/80' : 'border-slate-200 bg-slate-50'}`} title={`${bucket.week_label || bucket.week_start || 'Woche'}: ${number(credits)} Credits, ${money(openAi)} OpenAI`}>
                    <span className="absolute inset-x-1 top-1 hidden rounded bg-slate-950 px-1 py-0.5 text-center text-[9px] text-white group-hover:block">{number(credits)} cr</span>
                    <span className="w-2.5 rounded-t bg-orange-500 transition-all" style={{ height: `${creditHeight}%` }} />
                    <span className="w-2.5 rounded-t bg-slate-400 transition-all dark:bg-slate-500" style={{ height: `${costHeight}%` }} />
                  </div>
                  <span className={`max-w-full truncate text-[10px] ${muted}`}>{bucket.week_label || bucket.week_start || '–'}</span>
                </div>
              );
            })}
          </div>
        ) : <div className={`mt-8 rounded-lg border border-dashed p-8 text-center text-sm ${dark ? 'border-gray-700 text-gray-400' : 'border-slate-300 text-slate-500'}`}>Noch keine Wochendaten vorhanden.</div>}
        <div className={`mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs ${muted}`}>
          <span className="inline-flex items-center gap-2"><i className="h-2.5 w-2.5 rounded-sm bg-orange-500" />LiveAvatar Credits</span>
          <span className="inline-flex items-center gap-2"><i className="h-2.5 w-2.5 rounded-sm bg-slate-400 dark:bg-slate-500" />OpenAI EUR (eigene Skala)</span>
        </div>
      </div>

      <aside className={`rounded-xl border p-5 shadow-sm xl:col-span-4 ${panel}`} aria-label="LiveAvatar Planauslastung">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-orange-500">Monatskontingent</p>
        <div className="mt-5 flex items-center gap-5">
          <div
            className="relative h-32 w-32 shrink-0 rounded-full"
            style={{ background: `conic-gradient(#f97316 0 ${Math.min(100, utilization)}%, ${dark ? '#1f2937' : '#e2e8f0'} ${Math.min(100, utilization)}% 100%)` }}
            role="img"
            aria-label={`${number(utilization)} Prozent des LiveAvatar-Kontingents verbraucht`}
          >
            <div className={`absolute inset-3 flex flex-col items-center justify-center rounded-full ${dark ? 'bg-gray-950' : 'bg-white'}`}>
              <strong className={`text-2xl tabular-nums ${text}`}>{number(utilization)}%</strong>
              <span className={`text-[10px] ${muted}`}>verbraucht</span>
            </div>
          </div>
          <dl className="min-w-0 space-y-3 text-xs">
            <div><dt className={muted}>Erfasst</dt><dd className={`mt-0.5 text-base font-semibold tabular-nums ${text}`}>{number(consumed)} cr</dd></div>
            <div><dt className={muted}>Inklusive</dt><dd className={`mt-0.5 font-semibold tabular-nums ${text}`}>{number(included)} cr</dd></div>
            <div><dt className={muted}>Verbleibend</dt><dd className={`mt-0.5 font-semibold tabular-nums ${text}`}>{number(Math.max(0, included - consumed))} cr</dd></div>
          </dl>
        </div>
        <div className={`mt-5 rounded-lg border px-3 py-2.5 text-xs leading-relaxed ${dark ? 'border-gray-800 bg-gray-900 text-gray-400' : 'border-slate-200 bg-slate-50 text-slate-600'}`}>
          Kontingentverbrauch ist keine zusätzliche Rechnung. Der feste Tarif wird separat ausgewiesen.
        </div>
      </aside>
    </section>
  );
}
